'use client'

import { MouseEvent } from 'react'
import { Divider, Spacer } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { useRequest } from '@marketplace-web/environment/request-context-data'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { urlWithParams } from '@marketplace-web/shared/url-util'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import {
  buildSubcatalogUrlParams,
  FacetedCategoryDto,
  CatalogAttribute,
} from '@marketplace-web/catalog/catalog-data'
import { useCatalogContext, selectCatalogEvent } from '@marketplace-web/catalog/catalog-page-data'

import {
  clickSecondaryNavigationEvent,
  viewSecondaryNavigationEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'

const SubcatalogNavigationFaceted = () => {
  const { searchParams } = useBrowserNavigation()
  const { track } = useTracking()
  const {
    state: {
      filters,
      dynamicFilters: { selectedDynamicFilters },
      facetedCategories: { categories },
    },
  } = useCatalogContext()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const { isBot } = useRequest()

  const urlParams = buildSubcatalogUrlParams({
    extraParams: searchParams,
    filters,
  })

  const handleClick = (category: FacetedCategoryDto, index: number) => (event: MouseEvent) => {
    event.preventDefault()

    const ids = [Number(category.id)]

    const { searchSessionId, globalCatalogBrowseSessionId, globalSearchSessionId } =
      getSearchSessionData()

    track(
      selectCatalogEvent({
        catalogIds: ids,
        attributeId: CatalogAttribute.Subcatalog,
      }),
    )
    track(
      clickSecondaryNavigationEvent({
        catalogId: ids[0]!,
        facetsCount: category.item_count || 0,
        position: index + 1,
        searchSessionId: searchSessionId || '',
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
    if (isBot) {
      navigateToPage(category.url)
    } else {
      fetchDynamicFilters({ selectedDynamicFilters, selectedFilters: { catalogIds: ids } })
    }
  }

  const handleInView = (category: FacetedCategoryDto, index: number) => (inView: boolean) => {
    if (!inView) return

    const { searchSessionId, globalCatalogBrowseSessionId, globalSearchSessionId } =
      getSearchSessionData()

    track(
      viewSecondaryNavigationEvent({
        catalogId: Number(category.id),
        facetsCount: category.item_count || 0,
        position: index + 1,
        searchSessionId: searchSessionId || '',
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  if (!categories) return undefined

  return (
    <>
      {categories.length > 0 && (
        <>
          <Divider />
          <Spacer size={Spacer.Size.XSmall} />
        </>
      )}
      <ul
        className="nav nav-detailed nav-page-categories"
        data-testid="sub-catalog-navigation-links"
      >
        {categories.map((category, index) => (
          <li className="nav-item" key={category.id}>
            <InView onChange={handleInView(category, index)}>
              <a
                className="nav-link u-ellipsis u-block"
                href={isBot ? category.url : urlWithParams(category.url || '', urlParams)}
                onClick={handleClick(category, index)}
              >
                {category.title}
              </a>
            </InView>
          </li>
        ))}
      </ul>
    </>
  )
}

export default SubcatalogNavigationFaceted
